/**
 * Agency Account Slide-over Alpine.js Component
 *
 * Displays agency accounting details: balance, VAT liability,
 * this month's income, receivable, invoices this month, and
 * combined transactions with infinite scroll.
 */

import { transactionSearchMixin } from './transaction_search_mixin'

export function agencyAccountSlideover() {
  const searchMixin = transactionSearchMixin('agency')
  const _performTransactionSearch = searchMixin.performSearch

  return {
    ...searchMixin,

    // Override performSearch to be tab-aware
    async performSearch() {
      if (this.activeTab === 'invoices') {
        this._performInvoiceSearch()
      } else {
        return _performTransactionSearch.call(this)
      }
    },

    // Client-side invoice search
    _performInvoiceSearch() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) {
        this.searchMatches = []
        this.searchMatchIndex = -1
        this.searchTotalMatches = 0
        this.highlightedTxnId = null
        return
      }

      const matches = this.invoicesThisMonth.filter(inv => {
        const desc = (inv.description || '').toLowerCase()
        const net = this.formatPence(inv.net_amount_pence).toLowerCase()
        const vat = this.formatPence(inv.vat_amount_pence).toLowerCase()
        const prop = (inv.property_name || '').toLowerCase()
        const entity = (inv.entity_name || '').toLowerCase()
        const date = this.formatDate(inv.date).toLowerCase()
        return desc.includes(query) || net.includes(query) || vat.includes(query) || prop.includes(query) || entity.includes(query) || date.includes(query)
      })

      this.searchMatches = matches.map(m => ({ id: `inv-${m.id}` }))
      this.searchTotalMatches = matches.length

      if (matches.length > 0) {
        this.searchMatchIndex = 0
        this.navigateToMatch(0)
      } else {
        this.searchMatchIndex = -1
        this.highlightedTxnId = null
      }
    },

    // State
    open: false,
    aiPanelOpen: false,
    loading: false,
    loadingMore: false,
    error: null,
    zIndex: 100010,

    // Data
    agencyId: null,
    agency: null,
    stats: null,
    invoicesThisMonth: [],
    transactions: [],

    // Tabs
    activeTab: 'transactions',

    // Pagination (transactions)
    page: 1,
    hasMore: false,

    // Kebab menu
    menuOpen: false,
    showBackToTop: false,

    // Open — receives { id } from event detail
    openSlideover(detail) {
      const id = detail?.id
      if (!id) return

      const store = Alpine.store?.('slideovers')

      // Bring to front if already open for this agency
      if (store?.isOpen?.('agency-account', id)) {
        store.bringToFront('agency-account', id)
        return
      }

      // Get next z-index
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.agencyId = id
      this.open = true
      this.activeTab = 'transactions'
      this.menuOpen = false
      this.clearSearch()
      this.transactions = []
      this.invoicesThisMonth = []
      this.page = 1
      this.hasMore = false

      // Register
      if (store?.register) {
        store.register('agency-account', id, this)
      }

      this.fetchData(true)
    },

    // Close the slideover
    close() {
      const store = Alpine.store?.('slideovers')
      if (store && this.agencyId) {
        store.unregister('agency-account', this.agencyId)
      }

      this.menuOpen = false
      this.$nextTick(() => {
        this.open = false
        window.dispatchEvent(new CustomEvent('account-slideover-closed'))
      })
    },

    // Fetch data from controller
    async fetchData(initial = false) {
      if (initial) {
        this.loading = true
        this.page = 1
        this.transactions = []
      } else {
        this.loadingMore = true
      }
      this.error = null

      try {
        const params = new URLSearchParams()
        params.set('page', this.page)

        const response = await fetch(`/accounting/accounts/agencies/${this.agencyId}.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        this.agency = data.agency
        this.stats = data.stats
        this.invoicesThisMonth = data.invoices_this_month || []

        if (initial) {
          this.transactions = data.transactions?.lines || []
        } else {
          this.transactions = [...this.transactions, ...(data.transactions?.lines || [])]
        }

        this.hasMore = data.transactions?.pagination?.has_more || false
      } catch (error) {
        console.error('Failed to fetch agency account:', error)
        this.error = 'Failed to load agency account details. Please try again.'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    // Silent refresh — keeps existing content visible while fetching updated data
    async refreshData() {
      this.error = null

      try {
        const params = new URLSearchParams()
        params.set('page', 1)

        const response = await fetch(`/accounting/accounts/agencies/${this.agencyId}.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

        const data = await response.json()
        this.agency = data.agency
        this.stats = data.stats
        this.invoicesThisMonth = data.invoices_this_month || []
        this.transactions = data.transactions?.lines || []
        this.page = 1
        this.hasMore = data.transactions?.pagination?.has_more || false
      } catch (error) {
        console.error('Failed to refresh agency account:', error)
      }
    },

    setTab(tab) {
      if (tab === this.activeTab) return
      this.clearSearch()
      this.searchOpen = false
      this.activeTab = tab
    },

    // Load next page (transactions only)
    loadMore() {
      if (this.loadingMore || !this.hasMore) return
      this.page++
      this.fetchData(false)
    },

    // Infinite scroll handler
    handleScroll(event) {
      this.showBackToTop = window.shouldShowBackToTop(event)

      if (this.activeTab !== 'transactions') return

      const el = event.target
      const scrollPosition = el.scrollTop + el.clientHeight
      const threshold = el.scrollHeight - 200

      if (scrollPosition >= threshold && this.hasMore && !this.loadingMore) {
        this.loadMore()
      }
    },

    scrollToTop() {
      window.scrollToTopHelper(this.$refs.scrollContainer)
    },

    // Dynamic text size for stat cards
    get statValueClass() {
      if (!this.stats) return 'text-2xl'
      const lengths = [
        this.formatPence(this.stats.balance_pence).length,
        this.formatPence(this.stats.receivable_pence).length,
        this.formatPence(this.stats.this_month_pence).length
      ]
      const max = Math.max(...lengths)
      if (max > 9) return 'text-base'
      if (max > 7) return 'text-lg'
      return 'text-2xl'
    },

    // Stat card subtitle text
    get balanceSubtitle() {
      if (!this.stats) return ''
      const vat = this.stats.balance_vat_pence || 0
      if (this.stats.balance_pence === 0 && vat === 0) return 'Nothing to withdraw'
      return `+ ${this.formatPence(vat)} VAT`
    },

    get receivableSubtitle() {
      if (!this.stats) return ''
      const vat = this.stats.receivable_vat_pence || 0
      if (this.stats.receivable_pence === 0 && vat === 0) return 'All received'
      return `+ ${this.formatPence(vat)} VAT`
    },

    get thisMonthSubtitle() {
      if (!this.stats) return ''
      const vat = this.stats.this_month_vat_pence || 0
      return `+ ${this.formatPence(vat)} VAT`
    },

    // Current month/year display
    get currentMonthYear() {
      const date = new Date()
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      return `${months[date.getMonth()]} ${date.getFullYear()}`
    },

    // Invoice totals (computed client-side)
    get invoiceTotals() {
      let netTotal = 0
      let vatTotal = 0
      for (const inv of this.invoicesThisMonth) {
        netTotal += inv.net_amount_pence || 0
        vatTotal += inv.vat_amount_pence || 0
      }
      return { net_pence: netTotal, vat_pence: vatTotal }
    },

    // Format helpers
    formatPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return '\u00A3' + formatted
    },

    formatSignedPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const prefix = pence >= 0 ? '+' : '-'
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return `${prefix}\u00A3${formatted}`
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr + 'T00:00:00')
      const day = date.getDate()
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`
    },

    formatShortDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr + 'T00:00:00')
      const day = date.getDate()
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${day} ${months[date.getMonth()]}`
    },

    txnBarClass(txn) {
      if (txn.is_refund) return 'bg-red-400'
      if (txn.payment_status === 'unreconciled') return 'bg-amber-400'
      if (txn.charge_status === 'unsettled' || txn.charge_status === 'outstanding' || txn.charge_status === 'partial') return 'bg-amber-400'
      return 'bg-blue-400'
    },

    amountColorClass(pence) {
      if (pence > 0) return 'text-emerald-600 dark:text-emerald-400'
      if (pence < 0) return 'text-red-600 dark:text-red-400'
      return 'text-gray-500 dark:text-gray-400'
    }
  }
}
